import type { CdnProviderItem } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { RiEdit2Line, RiDeleteBinLine, RiAddLine, RiLink } from '@remixicon/react';
import { Button, Chip, Tooltip, useDisclosure } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { toast } from 'react-toastify';

import { TablePage, TableFilters, ColumnsToggleDropdown } from '@/components/table';
import { cdnProvidersListParamsAtom, cdnProvidersQueryAtom, setDefaultCdnProvider } from '@/store';

// Custom hooks
import {
  useCdnProvidersFilters,
  useCdnProvidersSorting,
  useCdnProviderEdit,
  useCdnProviderCreate,
  useCdnProviderDelete
} from '@/hooks/cdn-providers';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { CdnProviderFormModal } from '@/components/cdn-providers/CdnProviderFormModal';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { PermissionGuard } from '@/components/shared/PermissionGuard';

export default function CdnProvidersPage() {
  const { data, isLoading, isError, error } = useAtomValue(cdnProvidersQueryAtom);
  const [params, setParams] = useAtom(cdnProvidersListParamsAtom);

  // Custom hooks
  const {
    searchTerm,
    setSearchTerm,
    dateFilter,
    handlePageChange,
    handleDateChange,
    handleSearch,
    handleClearSearch,
    hasAnyFilter,
    clearFilters
  } = useCdnProvidersFilters();

  const { isEditOpen, editForm, setEditForm, isSaving, openEdit, saveEdit, onEditOpenChange } = useCdnProviderEdit();
  const { isCreateOpen, createForm, setCreateForm, isCreating, openCreate, saveCreate, onCreateOpenChange } =
    useCdnProviderCreate();
  const { isDeleteOpen, deletingProviderId, isDeleting, openDelete, confirmDelete, onDeleteOpenChange } =
    useCdnProviderDelete();

  // Set default modal state
  const { isOpen: isSetDefaultOpen, onOpen: onSetDefaultOpen, onOpenChange: onSetDefaultOpenChange } = useDisclosure();
  const [defaultProviderId, setDefaultProviderId] = React.useState<number | null>(null);
  const [, setDefaultProviderAtom] = useAtom(setDefaultCdnProvider);

  const cdnProviders = React.useMemo<CdnProviderItem[]>(() => data?.data ?? [], [data]);
  const { sortDescriptor, setSortDescriptor, sortedItems } = useCdnProvidersSorting(cdnProviders);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  // Set default handler
  const handleSetDefault = React.useCallback(async () => {
    if (!defaultProviderId) return;

    try {
      await setDefaultProviderAtom(defaultProviderId);
      toast.success(i18n.t('copy.cdn_provider_set_as_default_successfully'));
      onSetDefaultOpenChange();
      setDefaultProviderId(null);
    } catch (error) {
      toast.error(i18n.t('copy.failed_to_set_cdn_provider_as_default'));
    }
  }, [defaultProviderId, setDefaultProviderAtom, onSetDefaultOpenChange]);

  // Open set default modal
  const openSetDefault = React.useCallback(
    (providerId: number) => {
      setDefaultProviderId(providerId);
      onSetDefaultOpen();
    },
    [onSetDefaultOpen]
  );

  // Enhanced save functions
  const handleSaveEdit = React.useCallback(async () => {
    await saveEdit();
  }, [saveEdit]);

  const handleSaveCreate = React.useCallback(async () => {
    await saveCreate();
  }, [saveCreate]);

  const handleConfirmDelete = React.useCallback(async () => {
    await confirmDelete();
  }, [confirmDelete]);

  const renderCell = React.useCallback(
    (provider: CdnProviderItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex items-center gap-2">
              <RiLink className="text-primary h-4 w-4" />
              <span className="font-medium">{provider.name}</span>
            </div>
          );
        case 'base_url':
          return (
            <Tooltip content={provider.base_url}>
              <span className="text-default-600 max-w-[200px] truncate text-sm">{provider.base_url}</span>
            </Tooltip>
          );
        case 'is_default':
          return (
            <Chip color={provider.is_default ? 'success' : 'default'} size="sm" variant="flat">
              {provider.is_default ? 'Default' : 'Custom'}
            </Chip>
          );
        case 'type':
          return (
            <div className="flex items-center gap-2">
              {provider.is_default ? (
                <Chip color="success" size="sm" variant="flat">
                  Default
                </Chip>
              ) : (
                <PermissionGuard permission="CdnProvider.update">
                  <Button size="sm" variant="light" color="primary" onPress={() => openSetDefault(provider.id)}>
                    Set Default
                  </Button>
                </PermissionGuard>
              )}
            </div>
          );
        case 'actions':
          return (
            <div className="relative flex items-center justify-end">
              <PermissionGuard permission="CdnProvider.update">
                <Tooltip content={i18n.t('copy.edit_provider')}>
                  <Button isIconOnly size="sm" variant="light" onPress={() => openEdit(provider)}>
                    <RiEdit2Line className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="CdnProvider.destroy">
                <Tooltip content={i18n.t('copy.delete_provider')}>
                  <Button isIconOnly size="sm" variant="light" color="danger" onPress={() => openDelete(provider)}>
                    <RiDeleteBinLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [openEdit, openDelete, openSetDefault]
  );

  const columns = React.useMemo(
    () => [
      { name: i18n.t('columns.name'), uid: 'name', sortable: true },
      { name: i18n.t('copy.base_url'), uid: 'base_url', sortable: true },
      { name: i18n.t('filters.type'), uid: 'type', sortable: false },
      { name: i18n.t('common.actions'), uid: 'actions', align: 'end' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedItems}
      emptyMessage={i18n.t('copy.no_cdn_providers_found')}
      error={error}
      errorMessage={i18n.t('copy.failed_to_load_cdn_providers')}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchInput
            placeholder={i18n.t('copy.search_cdn_providers')}
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          {/* <DateRangeFilter
            value={dateFilter}
            onChange={handleDateChange}
          /> */}
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor || { column: 'name', direction: 'ascending' }}
      title={i18n.t('copy.cdn_providers')}
      titleActions={
        <Button
          color="success"
          size="sm"
          variant="flat"
          startContent={<RiAddLine className="h-4 w-4" />}
          onPress={openCreate}
        >
          Add Provider
        </Button>
      }
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={setSortDescriptor}
    >
      {/* Edit Modal */}
      <CdnProviderFormModal
        isOpen={isEditOpen}
        onOpenChange={onEditOpenChange}
        formData={editForm}
        setFormData={setEditForm}
        isSubmitting={isSaving}
        onSubmit={handleSaveEdit}
        title={i18n.t('copy.edit_cdn_provider')}
        submitText="Update"
      />

      {/* Create Modal */}
      <CdnProviderFormModal
        isOpen={isCreateOpen}
        onOpenChange={onCreateOpenChange}
        formData={createForm}
        setFormData={setCreateForm}
        isSubmitting={isCreating}
        onSubmit={handleSaveCreate}
        title={i18n.t('copy.add_cdn_provider')}
        submitText="Create"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        title={i18n.t('copy.delete_cdn_provider')}
        message={`Are you sure you want to delete this CDN provider? This action cannot be undone.`}
        confirmText={i18n.t('common.delete')}
        cancelText="Cancel"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDeleteModal
        isOpen={isSetDefaultOpen}
        onOpenChange={onSetDefaultOpenChange}
        title={i18n.t('copy.set_default_cdn_provider')}
        message="Are you sure you want to set this CDN provider as the default? This will make it the primary provider for new domains."
        confirmText={i18n.t('copy.set_default')}
        cancelText="Cancel"
        isLoading={false}
        onConfirm={handleSetDefault}
      />
    </TablePage>
  );
}
